/**
 * ExpandTool — standalone /tools/expand page.
 * Public tool: paste short text, choose tone + length, get an expanded
 * draft. Great for landlord message drafts, listing descriptions,
 * maintenance replies, or tenant emails.
 */
import { useState } from "react";
import { Sparkles, Copy, Check, RotateCcw, Wand2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PublicHeader from "@/components/layout/PublicHeader";
import { usePageTitle } from "@/lib/seo";

const TONES = [
  { key: "professional", label: "Professional" },
  { key: "friendly",     label: "Friendly" },
  { key: "concise",      label: "Concise" },
  { key: "formal",       label: "Formal" },
];
const LENGTHS = [
  { key: "short",  label: "Short (~40 words)" },
  { key: "medium", label: "Medium (~90 words)" },
  { key: "long",   label: "Long (~180 words)" },
];

export default function ExpandTool() {
  usePageTitle("Generative Expand — Central Rentals Canada");
  const [seed, setSeed] = useState("");
  const [tone, setTone] = useState("professional");
  const [length, setLength] = useState("medium");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [copied, setCopied] = useState(false);

  const expand = async () => {
    const s = seed.trim();
    if (!s) { toast.info("Paste a few sentences first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auto/expand", { text: s, tone, length });
      setResult((data?.expanded || "").trim());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not expand");
    } finally {
      setBusy(false);
    }
  };

  const doCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch { /* clipboard restricted */ }
  };

  const reset = () => { setSeed(""); setResult(""); };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader light />
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-14" data-testid="expand-tool">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-200 text-[11px] font-bold tracking-[0.18em] uppercase text-emerald-800">
          <Wand2 className="w-3.5 h-3.5"/> Generative Expand
        </div>
        <h1 className="page-hero-h1 mt-4 text-slate-900">Turn a short note into a polished draft.</h1>
        <p className="page-hero-sub mt-3 max-w-2xl">
          Paste any short message, listing blurb, or maintenance reply. Pick a tone + length. Copilot expands it — without inventing facts.
        </p>

        <div className="mt-8 bg-white rounded-3xl shadow-lg border border-slate-200 p-5 md:p-8">
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Your seed</span>
            <textarea
              data-testid="expand-tool-seed"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              rows={5}
              placeholder="e.g. Thanks for reporting the leak — I've booked a plumber tomorrow at 10am."
              className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-y"
            />
          </label>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Tone</span>
              <select
                data-testid="expand-tool-tone"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="mt-2 w-full rounded-full border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {TONES.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Length</span>
              <select
                data-testid="expand-tool-length"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                className="mt-2 w-full rounded-full border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {LENGTHS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
              </select>
            </label>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={expand}
              disabled={busy || !seed.trim()}
              data-testid="expand-tool-run"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Sparkles className="w-4 h-4"/> {busy ? "Expanding…" : "Expand"}
            </button>
            {(seed || result) && (
              <button type="button" onClick={reset} data-testid="expand-tool-reset"
                className="inline-flex items-center gap-1.5 px-4 py-3 rounded-full border border-slate-300 hover:border-slate-500 text-slate-700 font-semibold text-sm transition-colors">
                <RotateCcw className="w-4 h-4"/> Reset
              </button>
            )}
          </div>
        </div>

        {result && (
          <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-3xl p-5 md:p-8" data-testid="expand-tool-output">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-800">Expanded draft</span>
              <button
                type="button"
                onClick={doCopy}
                data-testid="expand-tool-copy"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-white hover:bg-slate-50 border border-emerald-300 text-emerald-800 transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5"/> : <Copy className="w-3.5 h-3.5"/>}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="whitespace-pre-wrap text-slate-800 leading-relaxed text-base">{result}</p>
          </div>
        )}
      </div>
    </div>
  );
}
